/**
 * Immutable tree operations for the form designer.
 *
 * Layouts may be nested recursively in columns and panels; fields may live in
 * columns, panels or table definitions. Every operation returns a new form so
 * React detects the change and circular drag/drop moves are rejected.
 */
import type { DynamicFormDefinition, FormColumn, FormField, FormRow, FormTab } from '../../models/form';
import { createId } from '../../utils/id';

export interface FieldLocation {
  tabName: string;
  rowId: string;
  columnId: string;
  parentFieldId?: string;
  table?: boolean;
  index: number;
}

export interface LayoutLocation {
  tabName: string;
  parentColumnId?: string;
  parentFieldId?: string;
  index: number;
}

export type DesignerSelection =
  | { kind: 'field'; id: string }
  | { kind: 'layout'; id: string };

type FieldVisitor = (field: FormField, index: number, siblings: FormField[], parent?: FormField, table?: boolean) => boolean | void;
type RowVisitor = (row: FormRow, index: number, siblings: FormRow[], parentColumn?: FormColumn, parentField?: FormField) => boolean | void;

function walkFields(fields: FormField[], visitor: FieldVisitor, parent?: FormField, table = false): boolean {
  for (let index = 0; index < fields.length; index += 1) {
    const field = fields[index];
    if (!field) continue;
    if (visitor(field, index, fields, parent, table)) return true;
    if (field.children && walkFields(field.children, visitor, field, false)) return true;
    if (field.tableColumns && walkFields(field.tableColumns, visitor, field, true)) return true;
    if (field.panelRows && walkAllFieldsInRows(field.panelRows, visitor)) return true;
    if (field.tableRows && walkAllFieldsInRows(field.tableRows, visitor)) return true;
  }
  return false;
}

function walkPanelRowsInFields(fields: FormField[], visitor: RowVisitor): boolean {
  for (const field of fields) {
    if (field.panelRows && walkRows(field.panelRows, visitor, undefined, field)) return true;
    if (field.tableRows && walkRows(field.tableRows, visitor, undefined, field)) return true;
    if (field.children && walkPanelRowsInFields(field.children, visitor)) return true;
    if (field.tableColumns && walkPanelRowsInFields(field.tableColumns, visitor)) return true;
  }
  return false;
}

function walkRows(rows: FormRow[], visitor: RowVisitor, parentColumn?: FormColumn, parentField?: FormField): boolean {
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    if (!row) continue;
    if (visitor(row, index, rows, parentColumn, parentField)) return true;
    for (const column of row.columns) {
      if (walkPanelRowsInFields(column.fields, visitor)) return true;
      if (column.rows && walkRows(column.rows, visitor, column)) return true;
    }
  }
  return false;
}

function walkColumns(rows: FormRow[], visitor: (column: FormColumn, row: FormRow) => boolean | void): boolean {
  for (const row of rows) {
    for (const column of row.columns) {
      if (visitor(column, row)) return true;
      for (const field of column.fields) {
        if (field.panelRows && walkColumns(field.panelRows, visitor)) return true;
        if (field.tableRows && walkColumns(field.tableRows, visitor)) return true;
        if (field.children && walkPanelColumnsInFields(field.children, visitor)) return true;
      }
      if (column.rows && walkColumns(column.rows, visitor)) return true;
    }
  }
  return false;
}

function walkPanelColumnsInFields(fields: FormField[], visitor: (column: FormColumn, row: FormRow) => boolean | void): boolean {
  for (const field of fields) {
    if (field.panelRows && walkColumns(field.panelRows, visitor)) return true;
    if (field.tableRows && walkColumns(field.tableRows, visitor)) return true;
    if (field.children && walkPanelColumnsInFields(field.children, visitor)) return true;
    if (field.tableColumns && walkPanelColumnsInFields(field.tableColumns, visitor)) return true;
  }
  return false;
}

function walkAllFieldsInRows(rows: FormRow[], visitor: FieldVisitor): boolean {
  for (const row of rows) {
    for (const column of row.columns) {
      if (walkFields(column.fields, visitor)) return true;
      if (column.rows && walkAllFieldsInRows(column.rows, visitor)) return true;
    }
  }
  return false;
}

function findColumnInTab(tab: FormTab, columnId: string): FormColumn | undefined {
  let result: FormColumn | undefined;
  walkColumns(tab.rows, (column) => {
    if (column.id === columnId) {
      result = column;
      return true;
    }
  });
  return result;
}

/** Finds a field recursively across columns, panels, panel layouts and tables. */
export function findField(form: DynamicFormDefinition, fieldId?: string): FormField | undefined {
  if (!fieldId) return undefined;
  let result: FormField | undefined;
  for (const tab of form.Tabs) {
    if (walkAllFieldsInRows(tab.rows, (field) => {
      if (field.id === fieldId) {
        result = field;
        return true;
      }
    })) return result;
  }
  return result;
}

/** Immutably updates a field wherever it appears in the designer tree. */
export function updateField(form: DynamicFormDefinition, fieldId: string, updater: (field: FormField) => FormField): DynamicFormDefinition {
  const clone = structuredClone(form);
  for (const tab of clone.Tabs) {
    if (walkAllFieldsInRows(tab.rows, (field, index, siblings) => {
      if (field.id === fieldId) {
        siblings[index] = updater(field);
        return true;
      }
    })) return clone;
  }
  return clone;
}

function scanFieldLocations(
  rows: FormRow[],
  tabName: string,
  visitor: (field: FormField, index: number, siblings: FormField[], location: FieldLocation) => boolean | void
): boolean {
  const scanFields = (fields: FormField[], rowId: string, columnId: string, parent?: FormField, table = false): boolean => {
    for (let index = 0; index < fields.length; index += 1) {
      const field = fields[index];
      if (!field) continue;
      const location: FieldLocation = { tabName, rowId, columnId, parentFieldId: parent?.id, table, index };
      if (visitor(field, index, fields, location)) return true;
      if (field.children && scanFields(field.children, rowId, columnId, field, false)) return true;
      if (field.tableColumns && scanFields(field.tableColumns, rowId, columnId, field, true)) return true;
      if (field.panelRows && scanRows(field.panelRows)) return true;
      if (field.tableRows && scanRows(field.tableRows)) return true;
    }
    return false;
  };

  const scanRows = (items: FormRow[]): boolean => {
    for (const row of items) {
      for (const column of row.columns) {
        if (scanFields(column.fields, row.id, column.id)) return true;
        if (column.rows && scanRows(column.rows)) return true;
      }
    }
    return false;
  };

  return scanRows(rows);
}

/** Removes a field and returns its former location so move operations can reinsert it. */
export function removeField(form: DynamicFormDefinition, fieldId: string): { form: DynamicFormDefinition; field?: FormField; location?: FieldLocation } {
  const clone = structuredClone(form);
  for (const tab of clone.Tabs) {
    let removed: FormField | undefined;
    let location: FieldLocation | undefined;
    const found = scanFieldLocations(tab.rows, tab.name, (field, index, siblings, current) => {
      if (field.id !== fieldId) return;
      removed = siblings.splice(index, 1)[0];
      location = current;
      return true;
    });
    if (found) return { form: clone, field: removed, location };
  }
  return { form: clone };
}

/** Inserts a field into a concrete layout column at an optional position. */
export function addFieldToColumn(
  form: DynamicFormDefinition,
  tabName: string,
  _rowId: string,
  columnId: string,
  field: FormField,
  index?: number
): DynamicFormDefinition {
  const clone = structuredClone(form);
  const tab = clone.Tabs.find((item) => item.name === tabName);
  const column = tab ? findColumnInTab(tab, columnId) : undefined;
  if (!column) return clone;
  if (index == null || index < 0 || index > column.fields.length) column.fields.push(field);
  else column.fields.splice(index, 0, field);
  return clone;
}

/** Inserts a field inside a panel or as a table column. */
export function addNestedField(form: DynamicFormDefinition, parentId: string, field: FormField, table = false, index?: number): DynamicFormDefinition {
  return updateField(form, parentId, (parent) => {
    const copy = { ...parent };
    const target = table ? [...(copy.tableColumns ?? [])] : [...(copy.children ?? [])];
    if (index == null || index < 0 || index > target.length) target.push(field);
    else target.splice(index, 0, field);
    if (table) copy.tableColumns = target;
    else copy.children = target;
    return copy;
  });
}

function rowsContainField(rows: FormRow[], descendantId: string): boolean {
  let found = false;
  walkAllFieldsInRows(rows, (field) => {
    if (field.id === descendantId) {
      found = true;
      return true;
    }
  });
  return found;
}

/** Detects field ancestry and prevents circular drag/drop moves. */
export function fieldContainsField(field: FormField, descendantId: string): boolean {
  if (field.id === descendantId) return true;
  return Boolean(field.children?.some((child) => fieldContainsField(child, descendantId))
    || field.tableColumns?.some((child) => fieldContainsField(child, descendantId))
    || (field.panelRows && rowsContainField(field.panelRows, descendantId))
    || (field.tableRows && rowsContainField(field.tableRows, descendantId)));
}

function findFieldLocation(form: DynamicFormDefinition, fieldId: string): FieldLocation | undefined {
  for (const tab of form.Tabs) {
    let location: FieldLocation | undefined;
    if (scanFieldLocations(tab.rows, tab.name, (field, _index, _siblings, current) => {
      if (field.id !== fieldId) return;
      location = current;
      return true;
    })) return location;
  }
  return undefined;
}

/** Moves a field between columns, panels and tables while preserving immutability. */
export function moveField(form: DynamicFormDefinition, fieldId: string, target: Omit<FieldLocation, 'index'> & { index?: number }): DynamicFormDefinition {
  const source = findField(form, fieldId);
  const sourceLocation = findFieldLocation(form, fieldId);
  if (!source || !sourceLocation) return form;
  if (target.parentFieldId && fieldContainsField(source, target.parentFieldId)) return form;

  let targetIndex = target.index;
  const sameParent = target.parentFieldId
    ? sourceLocation.tabName === target.tabName
      && sourceLocation.parentFieldId === target.parentFieldId
      && Boolean(sourceLocation.table) === Boolean(target.table)
    : sourceLocation.tabName === target.tabName
      && sourceLocation.rowId === target.rowId
      && sourceLocation.columnId === target.columnId
      && !sourceLocation.parentFieldId;
  if (sameParent && targetIndex != null && sourceLocation.index < targetIndex) targetIndex -= 1;

  const removed = removeField(form, fieldId);
  if (!removed.field) return form;
  if (target.parentFieldId) return addNestedField(removed.form, target.parentFieldId, removed.field, Boolean(target.table), targetIndex);
  return addFieldToColumn(removed.form, target.tabName, target.rowId, target.columnId, removed.field, targetIndex);
}

function getUsedFieldNames(form: DynamicFormDefinition): Set<string> {
  const names = new Set<string>();
  for (const tab of form.Tabs) walkAllFieldsInRows(tab.rows, (field) => { if (field.name) names.add(field.name); });
  return names;
}

function uniqueCopyName(name: string, usedNames: Set<string>): string {
  const base = `${name || 'field'}_copy`;
  let candidate = base;
  let index = 2;
  while (usedNames.has(candidate)) candidate = `${base}${index++}`;
  usedNames.add(candidate);
  return candidate;
}

function refreshFieldIds(item: FormField, usedNames: Set<string>): void {
  item.id = createId('field');
  item.name = uniqueCopyName(item.name, usedNames);
  item.options?.forEach((option) => { option.id = createId('opt'); });
  item.children?.forEach((child) => refreshFieldIds(child, usedNames));
  item.tableColumns?.forEach((child) => refreshFieldIds(child, usedNames));
  item.panelRows?.forEach((row) => refreshLayoutIds(row, usedNames));
  item.tableRows?.forEach((row) => refreshLayoutIds(row, usedNames));
}

/** Deep-clones a field subtree and regenerates all client-side ids/names. */
export function duplicateField(form: DynamicFormDefinition, fieldId: string): DynamicFormDefinition {
  const clone = structuredClone(form);
  const usedNames = getUsedFieldNames(form);
  for (const tab of clone.Tabs) {
    if (walkAllFieldsInRows(tab.rows, (field, index, siblings) => {
      if (field.id !== fieldId) return;
      const copy = structuredClone(field);
      refreshFieldIds(copy, usedNames);
      siblings.splice(index + 1, 0, copy);
      return true;
    })) return clone;
  }
  return clone;
}

/** Finds a row recursively, including layouts nested in columns and panels. */
export function findLayout(form: DynamicFormDefinition, layoutId?: string): FormRow | undefined {
  if (!layoutId) return undefined;
  let result: FormRow | undefined;
  for (const tab of form.Tabs) {
    if (walkRows(tab.rows, (row) => {
      if (row.id === layoutId) {
        result = row;
        return true;
      }
    })) return result;
  }
  return result;
}

export function updateLayout(form: DynamicFormDefinition, layoutId: string, updater: (row: FormRow) => FormRow): DynamicFormDefinition {
  const clone = structuredClone(form);
  for (const tab of clone.Tabs) {
    if (walkRows(tab.rows, (row, index, siblings) => {
      if (row.id === layoutId) {
        siblings[index] = updater(row);
        return true;
      }
    })) return clone;
  }
  return clone;
}

export function removeLayout(form: DynamicFormDefinition, layoutId: string): { form: DynamicFormDefinition; layout?: FormRow; location?: LayoutLocation } {
  const clone = structuredClone(form);
  for (const tab of clone.Tabs) {
    let removed: FormRow | undefined;
    let location: LayoutLocation | undefined;
    const found = walkRows(tab.rows, (row, index, siblings, parentColumn, parentField) => {
      if (row.id !== layoutId) return;
      removed = siblings.splice(index, 1)[0];
      location = { tabName: tab.name, parentColumnId: parentColumn?.id, parentFieldId: parentField?.id, index };
      return true;
    });
    if (found) return { form: clone, layout: removed, location };
  }
  return { form: clone };
}

export function addLayoutToTab(form: DynamicFormDefinition, tabName: string, layout: FormRow, index?: number): DynamicFormDefinition {
  const clone = structuredClone(form);
  const rows = clone.Tabs.find((tab) => tab.name === tabName)?.rows;
  if (!rows) return clone;
  if (index == null || index < 0 || index > rows.length) rows.push(layout);
  else rows.splice(index, 0, layout);
  return clone;
}

export function addLayoutToColumn(form: DynamicFormDefinition, tabName: string, columnId: string, layout: FormRow, index?: number): DynamicFormDefinition {
  const clone = structuredClone(form);
  const tab = clone.Tabs.find((item) => item.name === tabName);
  const column = tab ? findColumnInTab(tab, columnId) : undefined;
  if (!column) return clone;
  const rows = column.rows ?? (column.rows = []);
  if (index == null || index < 0 || index > rows.length) rows.push(layout);
  else rows.splice(index, 0, layout);
  return clone;
}

/** Inserts a Bootstrap layout directly inside a panel body. */
export function addLayoutToPanel(form: DynamicFormDefinition, parentFieldId: string, layout: FormRow, index?: number): DynamicFormDefinition {
  return updateField(form, parentFieldId, (parent) => {
    if (parent.type !== 'panel') return parent;
    const rows = [...(parent.panelRows ?? [])];
    if (index == null || index < 0 || index > rows.length) rows.push(layout);
    else rows.splice(index, 0, layout);
    return { ...parent, panelRows: rows };
  });
}

/** Inserts a Bootstrap layout into a table's row-entry template. */
export function addLayoutToTable(form: DynamicFormDefinition, parentFieldId: string, layout: FormRow, index?: number): DynamicFormDefinition {
  return updateField(form, parentFieldId, (parent) => {
    if (parent.type !== 'table') return parent;
    const rows = [...(parent.tableRows ?? [])];
    if (index == null || index < 0 || index > rows.length) rows.push(layout);
    else rows.splice(index, 0, layout);
    return { ...parent, tableRows: rows };
  });
}

function addLayoutToFieldContainer(form: DynamicFormDefinition, parentFieldId: string, layout: FormRow, index?: number): DynamicFormDefinition {
  const parent = findField(form, parentFieldId);
  if (parent?.type === 'table') return addLayoutToTable(form, parentFieldId, layout, index);
  return addLayoutToPanel(form, parentFieldId, layout, index);
}

function layoutContainsColumn(layout: FormRow, columnId: string): boolean {
  let found = false;
  walkColumns([layout], (column) => {
    if (column.id === columnId) {
      found = true;
      return true;
    }
  });
  return found;
}

function layoutContainsField(layout: FormRow, fieldId: string): boolean {
  return rowsContainField([layout], fieldId);
}

/** Moves a row between root tabs, nested columns and panel bodies. */
export function moveLayout(
  form: DynamicFormDefinition,
  layoutId: string,
  target: { tabName: string; columnId?: string; parentFieldId?: string; index?: number }
): DynamicFormDefinition {
  const source = findLayout(form, layoutId);
  if (!source) return form;
  if (target.columnId && layoutContainsColumn(source, target.columnId)) return form;
  if (target.parentFieldId && layoutContainsField(source, target.parentFieldId)) return form;

  const removed = removeLayout(form, layoutId);
  if (!removed.layout) return form;

  let targetIndex = target.index;
  const sameParent = removed.location?.tabName === target.tabName
    && (removed.location?.parentColumnId ?? undefined) === (target.columnId ?? undefined)
    && (removed.location?.parentFieldId ?? undefined) === (target.parentFieldId ?? undefined);
  if (sameParent && targetIndex != null && removed.location && removed.location.index < targetIndex) targetIndex -= 1;

  if (target.parentFieldId) return addLayoutToFieldContainer(removed.form, target.parentFieldId, removed.layout, targetIndex);
  if (target.columnId) return addLayoutToColumn(removed.form, target.tabName, target.columnId, removed.layout, targetIndex);
  return addLayoutToTab(removed.form, target.tabName, removed.layout, targetIndex);
}

function refreshLayoutIds(row: FormRow, usedNames: Set<string>): void {
  row.id = createId('row');
  for (const column of row.columns) {
    column.id = createId('col');
    column.fields.forEach((field) => refreshFieldIds(field, usedNames));
    column.rows?.forEach((child) => refreshLayoutIds(child, usedNames));
  }
}

/** Deep-clones a layout tree and regenerates row, column and field ids. */
export function duplicateLayout(form: DynamicFormDefinition, layoutId: string): DynamicFormDefinition {
  const source = findLayout(form, layoutId);
  if (!source) return form;
  const clone = structuredClone(source);
  refreshLayoutIds(clone, getUsedFieldNames(form));

  const removedProbe = removeLayout(form, layoutId);
  const location = removedProbe.location;
  if (!location) return form;
  if (location.parentFieldId) return addLayoutToFieldContainer(form, location.parentFieldId, clone, location.index + 1);
  if (location.parentColumnId) return addLayoutToColumn(form, location.tabName, location.parentColumnId, clone, location.index + 1);
  return addLayoutToTab(form, location.tabName, clone, location.index + 1);
}

export function updateTab(form: DynamicFormDefinition, tabName: string, updater: (tab: FormTab) => FormTab): DynamicFormDefinition {
  return { ...form, Tabs: form.Tabs.map((tab) => tab.name === tabName ? updater(tab) : tab) };
}

/** Lists stable `Tab.Field` paths available to conditions and display bindings. */
export function listBindableFields(form: DynamicFormDefinition): Array<{ value: string; label: string }> {
  const result: Array<{ value: string; label: string }> = [];
  const seen = new Set<string>();
  for (const tab of form.Tabs) {
    walkAllFieldsInRows(tab.rows, (field, _index, _siblings, parent, table) => {
      const bindableType = ['input', 'textarea', 'select', 'checkbox', 'radio', 'fileupload', 'table'].includes(field.type);
      if (!bindableType || !field.name) return;
      const value = `${tab.name}.${field.name}`;
      if (seen.has(value)) return;
      seen.add(value);
      const tableLabel = table && parent?.type === 'table' ? `${parent.designerTitle || parent.title || 'جدول'} / ` : '';
      const fieldLabel = field.designerTitle || field.title || field.name;
      result.push({ value, label: `${tab.title} / ${tableLabel}${fieldLabel}` });
    });
  }
  return result;
}
